import { Check, ChevronDown } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export type MultiSelectOption = {
  id: string;
  name: string;
  /** Tailwind class for a solid dot before the name. Omitted = no dot. */
  swatchClass?: string;
  /** Contextual count. Omitted where no meaningful count exists. */
  count?: number;
};

type Props = {
  options: MultiSelectOption[];
  selected: string[];
  onToggle: (id: string) => void;
  onClear: () => void;
  /** What the trigger reads with nothing selected ("Etiquetas", "Producto"). */
  placeholder: string;
  /** "{n} {pluralNoun}" once more than one is selected. */
  pluralNoun: string;
  /** "1 {singularNoun}" when the one selected value is not among the options. */
  singularNoun: string;
  /** The first menu item while something is selected. */
  clearLabel: string;
  icon?: LucideIcon;
  /** Accessible name of the trigger; defaults to the placeholder. */
  ariaLabel?: string;
  className?: string;
  /** Width of the open menu. Defaults to a fixed `w-56`; long option names
   * (programs) pass a content-sized width instead. */
  contentClassName?: string;
};

/**
 * A filter dropdown that keeps several values at once — values inside it are
 * OR'd by whoever reads it.
 *
 * Presentation only: it owns no data source and no filter state. The pipeline's
 * label filter and every /pedidos filter bind to it, so there is one copy of the
 * markup. The menu stays open on select, because picking several is the point.
 */
export function MultiSelectFilter({
  options,
  selected,
  onToggle,
  onClear,
  placeholder,
  pluralNoun,
  singularNoun,
  clearLabel,
  icon: Icon,
  ariaLabel,
  className,
  contentClassName = "w-56",
}: Props): React.JSX.Element {
  const selectedSet = new Set(selected);
  const triggerLabel =
    selectedSet.size === 0
      ? placeholder
      : selectedSet.size === 1
        ? (options.find((o) => selectedSet.has(o.id))?.name ?? `1 ${singularNoun}`)
        : `${selectedSet.size} ${pluralNoun}`;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel ?? placeholder}
          className={`flex h-10 w-full items-center justify-between gap-2 rounded-md border bg-white px-3 py-2 text-sm md:w-[200px] ${
            selectedSet.size > 0
              ? "border-moss text-ink-900"
              : "border-ink-200 text-ink-500"
          } ${className ?? ""}`}
        >
          <span className="flex items-center gap-1.5 truncate">
            {Icon && <Icon className="h-3.5 w-3.5 shrink-0" />}
            <span className="truncate">{triggerLabel}</span>
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className={`max-h-72 overflow-y-auto ${contentClassName}`}>
        {selectedSet.size > 0 && (
          <>
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                onClear();
              }}
              className="text-ink-500"
            >
              {clearLabel}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        {options.map((option) => (
          <DropdownMenuItem
            key={option.id}
            // Keep the menu open so several values can be toggled at once.
            onSelect={(e) => {
              e.preventDefault();
              onToggle(option.id);
            }}
            className="gap-2"
          >
            {option.swatchClass !== undefined && (
              <span className={`h-3 w-3 shrink-0 rounded-full ${option.swatchClass}`} />
            )}
            <span className="flex-1 truncate">
              {option.name}
              {option.count !== undefined && ` (${option.count})`}
            </span>
            {selectedSet.has(option.id) && <Check className="h-3.5 w-3.5 shrink-0" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
